import { FC } from "react";
import { useTranslation } from "react-i18next";
import GoogleMap from "google-map-react";

import { GOOGLE_MAPS_API_KEY } from "../../tabs/fixturesSegments/calibration/constants";
import { VenuePublic } from "../../../services/venueService";

const KEY = "leaderboards.VenuePage.";

/** Close enough to read the pitch markings without showing the neighbouring streets. */
const HERO_ZOOM = 16;

/**
 * Below this many players the counts read as an empty room rather than a community, so the
 * hero drops them and leads with the ground itself.
 */
const COUNTS_MIN_PLAYERS = 5;

export interface VenueHeroProps {
    venue: VenuePublic | null;
    /** The name the user typed on their own matches, used when the backend has none. */
    fallbackName?: string | null;
    /** The player's own tracked appearances at this ground. */
    apps: number;
    /**
     * All-time venue totals, from the venue doc - NOT the board snapshot, whose same-named
     * fields are scoped to the 90-day Local Legend window. The hero labels these without a
     * window, directly above the all-time leaderboard, so the two must agree.
     *
     * Shown only once `playerCount` clears `COUNTS_MIN_PLAYERS`.
     */
    playerCount?: number;
    entryCount?: number;
}

/**
 * The venue hero (SCORZA-739 design handoff, reference/02).
 *
 * A satellite map behind the venue name, reusing the same `google-map-react` setup as the pitch
 * calibration modal. It is decoration, not a map the user drives: every interaction is off, so
 * a drag scrolls the page rather than panning a map the user cannot do anything with, and the
 * tiles are `aria-hidden` because the venue name below them already says where this is.
 *
 * When the venue never resolved server-side there is no centroid to centre on, and the gradient
 * alone carries the hero — a plausible-looking map of the wrong place would be worse than none.
 */
export const VenueHero: FC<VenueHeroProps> = ({
    venue,
    fallbackName,
    apps,
    playerCount,
    entryCount,
}) => {
    const { t } = useTranslation();
    const centre = venue?.centroid;
    const name = venue?.name || fallbackName || t(KEY + "unnamedVenue");
    const showCounts = (playerCount ?? 0) > COUNTS_MIN_PLAYERS;

    return (
        <div className="venue-hero">
            <div className="venue-hero-map">
                {centre && (
                    <div
                        className="venue-hero-map-tiles"
                        aria-hidden="true"
                        data-testid="venue-hero-map"
                    >
                        <GoogleMap
                            bootstrapURLKeys={{ key: GOOGLE_MAPS_API_KEY }}
                            defaultCenter={{
                                lat: centre.latitude,
                                lng: centre.longitude,
                            }}
                            defaultZoom={HERO_ZOOM}
                            options={{
                                mapTypeId: "satellite",
                                disableDefaultUI: true,
                                draggable: false,
                                gestureHandling: "none",
                                keyboardShortcuts: false,
                                clickableIcons: false,
                                tilt: 0,
                            }}
                        />
                    </div>
                )}
                <div className="venue-hero-gradient" />
                <span className="venue-hero-pin" aria-hidden="true" />
                <div className="venue-hero-caption">
                    <div className="venue-hero-eyebrow">
                        {t(KEY + "venueEyebrow")}
                    </div>
                    <h1 data-testid="venue-name">{name}</h1>
                    <div className="venue-hero-locality">
                        {venue?.locality && (
                            <span data-testid="venue-locality">
                                {venue.locality}
                            </span>
                        )}
                        {apps > 0 && (
                            <span
                                className="venue-hero-apps"
                                data-testid="venue-hero-apps"
                            >
                                {t(KEY + "playedHere", { count: apps })}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {showCounts && (
                <div className="venue-hero-meta" data-testid="venue-counts">
                    {playerCount !== undefined && (
                        <div className="venue-hero-meta-tile">
                            <div className="venue-hero-meta-value">
                                {playerCount}
                            </div>
                            <div className="venue-hero-meta-label">
                                {t(KEY + "playersLabel")}
                            </div>
                        </div>
                    )}
                    {entryCount !== undefined && (
                        <div className="venue-hero-meta-tile">
                            <div className="venue-hero-meta-value">
                                {entryCount}
                            </div>
                            <div className="venue-hero-meta-label">
                                {t(KEY + "entriesLabel")}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
